/**
 * String Registry Filters — slide-out drawer (parallel UX to ConfigRegistryFiltersDrawer)
 */

import React, { useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Divider,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector, useDispatch } from "react-redux";
import {
  selectStringRegistryFilters,
  selectUISchema,
  selectIsFilterPanelOpen,
  setFilters,
  resetFilters,
  setFilterPanelOpen,
} from "../store/stringRegistryUISlice";
import {
  BglPrimarySmallButton,
  BglCloseOutlinedSmallButton,
} from "components/button/BglButtons";
import { StringRegistryFilters as FilterType } from "../types/stringRegistryTypes";
import { SearchableSelect } from "components/common/SearchableSelect";

const DRAWER_WIDTH = 320;

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "TODO", label: "To Do" },
  { value: "READY_FOR_QA", label: "Ready for QA" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

export const StringRegistryFiltersDrawer = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectStringRegistryFilters);
  const uiSchema = useSelector(selectUISchema);
  const isOpen = useSelector(selectIsFilterPanelOpen);

  const getLookupOptions = useCallback(
    (typeCode: string) => {
      if (!uiSchema?.lookupTypes) return [];
      const lt = uiSchema.lookupTypes.find((l) => l.code === typeCode);
      return lt?.lookups ?? [];
    },
    [uiSchema]
  );

  const handleFilterChange = (field: keyof FilterType, value: string) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleClose = () => dispatch(setFilterPanelOpen(false));
  const handleReset = () => dispatch(resetFilters());

  const componentOptions = getLookupOptions("COMPONENT");
  const sectionOptions = getLookupOptions("SECTION");

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          bgcolor: "background.paper",
        },
      }}
    >
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%", bgcolor: "background.paper" }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Filters</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Filter Fields */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, overflowY: "auto" }}>
          {/* Search */}
          <TextField
            label="Search"
            placeholder="Search by ID or name..."
            size="small"
            value={filters.search || ""}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            fullWidth
          />

          {/* Status */}
          <SearchableSelect
            size="small"
            fullWidth
            label="Status"
            value={filters.verificationStatus || ""}
            onChange={(v) => handleFilterChange("verificationStatus", v)}
            options={STATUS_OPTIONS}
          />

          {/* Component */}
          <SearchableSelect
            size="small"
            fullWidth
            label="Component"
            value={filters.component || ""}
            onChange={(v) => handleFilterChange("component", v)}
            options={[{ value: "", label: "All Components" }, ...componentOptions.map((opt) => ({ value: opt.documentId, label: opt.displayName }))]}
          />

          {/* Section */}
          <SearchableSelect
            size="small"
            fullWidth
            label="Section"
            value={filters.section || ""}
            onChange={(v) => handleFilterChange("section", v)}
            options={[{ value: "", label: "All Sections" }, ...sectionOptions.map((opt) => ({ value: opt.documentId, label: opt.displayName }))]}
          />
        </Box>

        {/* Footer Actions */}
        <Box sx={{ pt: 2, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <BglCloseOutlinedSmallButton onClick={handleReset}>
            Reset Filters
          </BglCloseOutlinedSmallButton>
          <BglPrimarySmallButton onClick={handleClose}>
            Apply
          </BglPrimarySmallButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default StringRegistryFiltersDrawer;
